import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  PlayCircle,
  Calendar,
  Clock,
  Search,
  ArrowUpDown,
  Info,
} from "lucide-react";
import axios from "axios";
import { logger } from "@/utils/logger";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import { SimpleTable } from "@/components/SimpleTable";
import { ColumnDef, PaginationState, Updater } from "@tanstack/react-table";
import { useDebounce } from "@/hooks/useDebounce";

interface PipelinesProps {
  apiBaseUrl: string;
}

// Helper functions from Sidebar.tsx for user display
const getInitials = (
  firstName?: string,
  lastName?: string,
  username?: string,
) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  if (username) {
    return username.substring(0, 2).toUpperCase();
  }
  return "??";
};

// Helper function to construct the full avatar URL (matching ArticlesListPage implementation)
const constructAvatarUrl = (
  apiBaseUrl: string,
  relativePath?: string | null,
): string | undefined => {
  if (!relativePath) return undefined;

  // If it's already an absolute URL (though backend should provide relative)
  if (relativePath.match(/^https?:\/\//)) {
    return relativePath;
  }

  // Derive the base URL for static assets by removing /api suffix and trailing slash
  const staticBaseUrl = apiBaseUrl.replace(/(\/api)?\/?$/, "");

  // Ensure no double slashes if relativePath starts with /
  const cleanRelativePath = relativePath.startsWith("/")
    ? relativePath.substring(1)
    : relativePath;
  return `${staticBaseUrl}/${cleanRelativePath}`;
};

// Status colors for pipeline runs
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "badge-success";
    case "phase_1_completed":
      return "badge-warning";
    case "failed":
    case "error":
      return "badge-error";
    case "running":
    case "started":
      return "badge-info";
    case "pending":
      return "badge-warning";
    default:
      return "badge-outline";
  }
};

// Progress colors for pipeline progress states
const getProgressColor = (status: string, currentStep?: string) => {
  if (currentStep) {
    return "badge-info"; // Running/active step
  }
  switch (status.toLowerCase()) {
    case "completed":
      return "badge-success";
    case "phase_1_completed":
      return "badge-warning";
    case "failed":
    case "error":
      return "badge-error";
    default:
      return "badge-outline";
  }
};

interface PipelineRunStep {
  step_name: string;
  status: string;
  message?: string;
  progress?: number;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  error?: string;
  timestamp: string;
}

interface PipelineRunLog {
  level: string;
  message: string;
  timestamp: string;
  source: string;
}

interface PipelineRunHistoryItem {
  id?: string;
  _id: string;
  user_id: string;
  username: string;
  user_info?: {
    username: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
    name?: string;
  };
  arguments: string[];
  triggered_at: string;
  status: string;
  current_step?: string;
  steps: PipelineRunStep[];
  logs: PipelineRunLog[];
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  exit_code?: number;
  error?: string;
  stored_data?: {
    analysis_queries?: any[];
    queries_count?: number;
    [key: string]: any;
  };
}

interface PaginatedPipelineRuns {
  runs: PipelineRunHistoryItem[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Column definition for pipeline runs table
interface GetColumnsProps {
  sortBy: string;
  sortDesc: boolean;
  setSortBy: (sortBy: string) => void;
  setSortDesc: (sortDesc: boolean) => void;
  setPage: (page: number) => void;
  apiBaseUrl: string;
}

const getColumns = ({
  sortBy,
  sortDesc,
  setSortBy,
  setSortDesc,
  setPage,
  apiBaseUrl,
}: GetColumnsProps): ColumnDef<PipelineRunHistoryItem>[] => [
  {
    accessorKey: "triggered_at",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "triggered_at" ? !sortDesc : true;
          setSortBy("triggered_at");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Started
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
        {sortBy === "triggered_at" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-xs text-base-content/70">
          <Calendar className="h-3 w-3" />
          <span className="font-medium">
            {new Date(row.original.triggered_at).toISOString().split("T")[0]}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-base-content/70">
          <Clock className="h-3 w-3" />
          {new Date(row.original.triggered_at).toLocaleTimeString()}
        </div>
      </div>
    ),
    meta: { style: { minWidth: "150px", width: "15%" } },
  },
  {
    accessorKey: "stored_data.queries_count",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc =
            sortBy === "stored_data.queries_count" ? !sortDesc : true;
          setSortBy("stored_data.queries_count");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Total Queries
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
        {sortBy === "stored_data.queries_count" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => {
      const isPhase1Completed = ["phase_1_completed", "completed"].includes(
        row.original.status,
      );
      const queriesCount = row.original.stored_data?.queries_count;

      return (
        <div className="flex justify-center">
          <span className="badge border-none font-mono text-xs text-info-content shadow-md badge-info">
            {isPhase1Completed && queriesCount ? queriesCount : "-"}
          </span>
        </div>
      );
    },
    meta: {
      style: { minWidth: "120px", width: "10%", textAlign: "center" as const },
    },
  },
  {
    accessorKey: "stored_data.analysis_queries",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          // Since this is complex data, we might sort by status or queries count instead
          const newSortDesc = sortBy === "status" ? !sortDesc : true;
          setSortBy("status");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Identified Release Versions
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
        {sortBy === "status" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => {
      const isPhase1Completed = ["phase_1_completed", "completed"].includes(
        row.original.status,
      );
      const analysisQueries = row.original.stored_data?.analysis_queries;

      if (!isPhase1Completed || !analysisQueries) {
        return (
          <div className="flex justify-center">
            <span className="badge badge-sm badge-warning">Analyzing</span>
          </div>
        );
      }

      // Extract unique release versions from analysis queries
      const releaseVersions = new Set<string>();
      analysisQueries.forEach((query: any) => {
        if (query?.release_item_version) {
          releaseVersions.add(query.release_item_version);
        }
      });

      const versions = Array.from(releaseVersions).slice(0, 4); // Show first 3

      return (
        <div className="flex flex-wrap gap-1">
          {versions.length > 0 ? (
            versions.map((version, index) => (
              <span
                key={index}
                className="badge badge-sm font-mono badge-primary"
              >
                {version}
              </span>
            ))
          ) : (
            <span className="badge-outline badge badge-sm">No versions</span>
          )}
          {releaseVersions.size > 4 && (
            <span className="badge-outline badge badge-sm">
              +{releaseVersions.size - 3} more
            </span>
          )}
        </div>
      );
    },
    meta: { style: { minWidth: "200px", width: "25%" } },
  },
  {
    accessorKey: "status",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "status" ? !sortDesc : true;
          setSortBy("status");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Status
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
        {sortBy === "status" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => {
      // Format status text: capitalize first letter and replace underscores with spaces
      const formatStatus = (status: string) => {
        return status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      };

      return (
        <div className="flex items-center gap-2">
          <span
            className={`badge ${getStatusColor(row.original.status)} border-none font-mono text-xs shadow-md`}
          >
            {formatStatus(row.original.status)}
          </span>
        </div>
      );
    },
    meta: { style: { minWidth: "120px", width: "12%" } },
  },
  {
    accessorKey: "user_info",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "username" ? !sortDesc : true;
          setSortBy("username");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        User
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
        {sortBy === "username" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => {
      const userInfo = row.original.user_info;

      // Construct display name like in Sidebar.tsx: full name from first_name + last_name, fallback to username
      const displayName =
        userInfo?.first_name || userInfo?.last_name
          ? `${userInfo?.first_name || ""} ${userInfo?.last_name || ""}`.trim()
          : userInfo?.username || row.original.username;

      const initials = getInitials(
        userInfo?.first_name,
        userInfo?.last_name,
        userInfo?.username || row.original.username,
      );

      const avatarUrl = constructAvatarUrl(apiBaseUrl, userInfo?.avatar_url);

      return (
        <div className="flex items-center gap-3">
          <div
            className={`size-8 flex-shrink-0 rounded-full ring-2 ring-base-300/50 ${
              !avatarUrl
                ? "flex items-center justify-center bg-primary/10 text-primary"
                : ""
            }`}
          >
            {avatarUrl ? (
              <img
                className="size-8 rounded-full"
                alt={displayName}
                src={avatarUrl}
              />
            ) : (
              <span className="text-xs font-medium">{initials}</span>
            )}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">{displayName}</span>
            {userInfo?.username && userInfo.username !== displayName && (
              <span className="truncate text-xs text-base-content/60">
                {userInfo.username}
              </span>
            )}
          </div>
        </div>
      );
    },
    meta: { style: { minWidth: "150px", width: "15%" } },
  },
  {
    accessorKey: "current_step",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "current_step" ? !sortDesc : true;
          setSortBy("current_step");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Progress
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
        {sortBy === "current_step" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => {
      const getProgressText = () => {
        if (row.original.current_step) {
          return (
            row.original.current_step.charAt(0).toUpperCase() +
            row.original.current_step.slice(1)
          );
        }
        switch (row.original.status) {
          case "completed":
            return "Complete";
          case "phase_1_completed":
            return "Phase 1 Complete";
          case "failed":
            return "Failed";
          default:
            return "—";
        }
      };

      const progressText = getProgressText();

      if (progressText === "—") {
        return <span className="text-base-content/50">—</span>;
      }

      return (
        <div className="flex items-center gap-2">
          <span
            className={`badge ${getProgressColor(row.original.status, row.original.current_step)} border-none font-mono text-xs shadow-md`}
          >
            {progressText}
          </span>
        </div>
      );
    },
    meta: { style: { minWidth: "150px", width: "15%" } },
  },
];

export function Pipelines({ apiBaseUrl }: PipelinesProps) {
  const { token } = useAuth();
  const { setBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();

  // State for data and UI
  const [data, setData] = useState<PaginatedPipelineRuns | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for pagination, sorting, and search
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortBy, setSortBy] = useState<string>("triggered_at");
  const [sortDesc, setSortDesc] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([{ label: "Pipeline Runs", href: "/pipeline" }]);
  }, [setBreadcrumbs]);

  // Fetch pipeline runs data
  const fetchData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      sort_by: sortBy,
      sort_desc: sortDesc.toString(),
    });
    if (debouncedSearchTerm) params.set("search_term", debouncedSearchTerm);

    try {
      const response = await axios.get<PaginatedPipelineRuns>(
        `${apiBaseUrl}/pipeline/history`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );
      setData(response.data);
      logger.info("Fetched pipeline runs:", response.data);
    } catch (err) {
      logger.error("Error fetching pipeline runs", {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/pipeline/history`,
        params: Object.fromEntries(params),
      });
      let errorMsg = "Failed to load pipeline runs.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Failed to load pipeline runs: ${err.response.data?.detail || err.message}`;
      } else if (err instanceof Error) {
        errorMsg = `Failed to load pipeline runs: ${err.message}`;
      }
      setError(errorMsg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    apiBaseUrl,
    page,
    pageSize,
    sortBy,
    sortDesc,
    debouncedSearchTerm,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Pagination handler
  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const newPaginationState =
      typeof updater === "function"
        ? updater({ pageIndex: page - 1, pageSize })
        : updater;

    if (newPaginationState.pageSize !== pageSize) {
      setPageSize(newPaginationState.pageSize);
      setPage(1);
    } else if (newPaginationState.pageIndex + 1 !== page) {
      setPage(newPaginationState.pageIndex + 1);
    }
  };

  // Row click handler
  const handleRowClick = useCallback(
    (row: PipelineRunHistoryItem) => {
      navigate(`/pipeline/${row._id}`);
    },
    [navigate],
  );

  // Add click handlers to table rows after they render
  useEffect(() => {
    const tableElement = document.querySelector(".pipeline-table tbody");
    if (!tableElement || !data?.runs) return;

    const handleClick = (e: Event) => {
      const row = (e.target as Element).closest("tr");
      if (row && !row.querySelector("th")) {
        const rowIndex = Array.from(row.parentElement!.children).indexOf(row);
        const pipelineRun = data.runs[rowIndex];
        if (pipelineRun) {
          handleRowClick(pipelineRun);
        }
      }
    };

    tableElement.addEventListener("click", handleClick);
    return () => {
      tableElement.removeEventListener("click", handleClick);
    };
  }, [data?.runs, handleRowClick]);

  // Columns
  const columns = React.useMemo(
    () =>
      getColumns({
        sortBy,
        sortDesc,
        setSortBy,
        setSortDesc,
        setPage,
        apiBaseUrl,
      }),
    [sortBy, sortDesc, setPage, apiBaseUrl],
  );

  // Render loading state
  if (loading && !data?.runs) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="loading loading-lg loading-spinner"></span>
        <p className="ml-4 text-base-content/70">Loading pipeline runs...</p>
      </div>
    );
  }

  // Render error state
  if (error && !data?.runs) {
    return (
      <div
        role="alert"
        className="mx-auto mt-8 alert max-w-2xl alert-error shadow-lg"
      >
        <AlertTriangle className="h-6 w-6" />
        <div>
          <h3 className="font-bold">Error Loading Data</h3>
          <div className="text-xs">{error}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 flex flex-col items-center justify-between gap-4 md:flex-row">
        <h1 className="text-2xl font-bold">
          Pipeline Runs ({loading ? "..." : (data?.total_count ?? 0)})
        </h1>
        <div className="flex items-center gap-2">
          <Link to="/pipeline/new" className="btn btn-sm btn-primary">
            <PlayCircle className="mr-2 h-4 w-4" />
            New Pipeline Run
          </Link>
          <div className="relative w-full md:w-64">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-base-content/40" />
            <input
              type="search"
              placeholder="Search pipeline runs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="input-bordered input input-sm w-full pl-9"
            />
          </div>
        </div>
      </div>

      {error && data?.runs && (
        <div role="alert" className="mb-4 alert alert-error">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Table */}
      {(data?.runs && data.runs.length > 0) || loading ? (
        <div className="pipeline-table [&_tbody_tr]:cursor-pointer [&_tbody_tr]:transition-colors [&_tbody_tr]:select-none [&_tbody_tr:hover]:bg-base-200/50">
          <SimpleTable
            columns={columns}
            data={data?.runs ?? []}
            isLoading={loading}
            skeletonRowCount={Math.max(10, pageSize / 2)}
            pageCount={data?.total_pages ?? 0}
            pagination={{ pageIndex: page - 1, pageSize }}
            onPaginationChange={handlePaginationChange}
          />
        </div>
      ) : (
        !loading && (
          <div className="py-10 text-center">
            <Info className="mx-auto mb-3 h-10 w-10 text-base-content/30" />
            <p className="font-semibold">No pipeline runs found.</p>
            <p className="text-sm text-base-content/60">
              Try adjusting your search criteria or{" "}
              <Link to="/pipeline/new" className="link link-primary">
                create a new pipeline run
              </Link>
              .
            </p>
          </div>
        )
      )}
    </div>
  );
}

export default Pipelines;
